/**
 * NavResizeHandle — the splitter between the nav rail and the stage.
 *
 * Rendered inside the stage container rather than on the rail so it lands in
 * the same place a panel-to-panel splitter would: half a gap before the first
 * panel's left edge. `.nav-resize-handle` in panels.css owns that geometry and
 * mirrors `.panel-resize-handle`'s grip treatment.
 *
 * Unlike the panel splitters, this one commits to the store on every move
 * instead of previewing through a CSS variable: the rail's width decides
 * whether labels render and how much width the stage gets, and both should
 * follow the pointer rather than appear at release. The cost is bounded —
 * inside the snap zone `previewNavWidth` yields only the two ends it can
 * settle to, so per-pixel updates happen only in the free zone above it, and
 * setNavWidthAtom skips a write that changes nothing.
 */

import { useAtomValueRawSync, useSetAtom } from 'jotai';
import type { PointerEvent as ReactPointerEvent, ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';

import { beginStageResize, endStageResize } from '../stageMotion';
import { usePointerDrag } from '@/hooks/usePointerDrag';
import { cn } from '@/lib/utils';
import { navWidthAtom, setNavWidthAtom } from '@/state/ui/shellRecordAtoms';

import { previewNavWidth, snapNavWidth } from './navResize';

interface NavResizeHandleProps {
    onResizeChange?: (isResizing: boolean) => void;
}

/** Where the drag started, and the width the rail had then. */
interface NavDrag {
    startX: number;
    startWidth: number;
}

export const NavResizeHandle = ({ onResizeChange }: NavResizeHandleProps): ReactElement => {
    const navWidth = useAtomValueRawSync(navWidthAtom);
    const setNavWidth = useSetAtom(setNavWidthAtom);
    const dragRef = useRef<NavDrag | null>(null);
    const elRef = useRef<HTMLDivElement | null>(null);

    const finish = (nextWidth?: number): void => {
        if (!dragRef.current) {
            return;
        }
        dragRef.current = null;
        elRef.current?.removeAttribute('data-nav-resizing');
        if (nextWidth !== undefined) {
            setNavWidth(nextWidth);
        }
        endStageResize();
        onResizeChange?.(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount only: a re-run ends the drag
    useEffect(() => () => finish(), []);

    const { onPointerDown } = usePointerDrag({
        onStart: (event: ReactPointerEvent<HTMLDivElement>): void => {
            dragRef.current = { startX: event.clientX, startWidth: navWidth };
            elRef.current?.setAttribute('data-nav-resizing', '');
            beginStageResize();
            onResizeChange?.(true);
        },
        onMove: (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (drag) {
                setNavWidth(previewNavWidth(drag.startWidth + (event.clientX - drag.startX)));
            }
        },
        onEnd: (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (drag) {
                finish(snapNavWidth(drag.startWidth + (event.clientX - drag.startX)));
            }
        },
        onCancel: (): void => finish(dragRef.current?.startWidth),
    });

    return (
        <div
            ref={elRef}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize navigation"
            onPointerDown={onPointerDown}
            className="nav-resize-handle group touch-none"
        >
            {/* The grip is the hover: a fill would paint the gap beside the rail. */}
            <div className={cn(
                'panel-resize-handle-grip h-8 w-[3px] rounded-full',
                'bg-occ-foreground-tertiary opacity-0 group-hover:opacity-100',
            )}
            />
        </div>
    );
};
